/** Canonical API locales; `zh` is accepted only as a legacy UI alias. */
import type { DirectorLanguage, DirectorResponse } from '@clutch/core';

export type UiLanguage = DirectorLanguage;
export const UI_LANGUAGES: readonly UiLanguage[] = ['en', 'hi', 'zh-Hans'];
export const isChineseLanguage = (l: string) => l === 'zh-Hans' || l === 'zh';
export function normalizeLanguage(l: unknown): DirectorLanguage | undefined {
  return l === 'en' || l === 'hi' ? l : l === 'zh' || l === 'zh-Hans' ? 'zh-Hans' : undefined;
}
const LABEL: Record<UiLanguage, string> = { en: 'EN', hi: 'हिन्दी', 'zh-Hans': '中文' };
export const languageLabel = (l: string) => LABEL[normalizeLanguage(l) ?? 'en'];
export const htmlLang = (l: string) => normalizeLanguage(l) ?? 'en';

/** Only offer supported server languages; ignore unknown future locales. */
export function offeredLanguages(capabilities?: { languages?: readonly string[]; locales?: readonly string[] }, fallback: readonly UiLanguage[] = ['en']): UiLanguage[] {
  const listed = (capabilities?.locales ?? capabilities?.languages)?.map(normalizeLanguage).filter((l): l is DirectorLanguage => !!l);
  const base = listed ?? fallback;
  return UI_LANGUAGES.filter((l) => base.includes(l));
}

/** Missing Chinese story text is explicit; it must never become English narration. */
export function loc(text: Partial<Record<string, string>> | undefined, l: string): string {
  if (isChineseLanguage(l)) return text?.['zh-Hans']?.trim() || '此故事暂时没有中文翻译。';
  return text?.[l] ?? text?.en ?? '';
}

/** UI tables retain their concise `zh` key; API payloads always use `zh-Hans`. */
export function pick<T>(table: { en: T; hi: T; zh?: T }, l: string): T {
  return l === 'hi' ? table.hi : isChineseLanguage(l) ? table.zh ?? table.en : table.en;
}
export const asDirectorLanguage = (l: UiLanguage): DirectorLanguage => normalizeLanguage(l) ?? 'en';

/** Old saved stories remain readable in their original language, without fabricated translations. */
export function storyHasTranslation(d: DirectorResponse): boolean {
  if (!isChineseLanguage(d.language)) return true;
  return d.moments.every((m) => !!m.title['zh-Hans']?.trim() && !!m.text['zh-Hans']?.trim())
    && d.segments.every((s) => !!s.text.trim() && /\p{Script=Han}/u.test(s.text));
}
